import Link from "next/link";
import type { ComponentProps } from "react";
import type { ButtonStoryblok } from "@/component-types-sb";

import { Headline } from "@/components/typography/Headline";
import { Button } from "@/components/ui/button";
import { Shape2 } from "@/components/illustrations/Shape2";
import { Shape3 } from "@/components/illustrations/Shape3";
import { cn } from "@/lib/utils";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";

export type HeroShape = "shape2" | "shape3";
export type HeroColor = "sand" | "sand-100" | "sky" | "sun";

type HeroProps = {
  headline: string;
  cta?: ButtonStoryblok;
  shape?: HeroShape;
  color?: HeroColor;
} & ComponentProps<"section">;

const Hero = ({
  headline,
  cta,
  shape = "shape3",
  color = "sand",
  ...rest
}: HeroProps) => {
  const Shape = shape === "shape3" ? Shape3 : Shape2;
  const url = getUrlFromLinkObject(cta?.link);

  return (
    <section
      className={cn(
        "px-4 py-6 md:p-16",
        { "bg-sand-50": color === "sand" },
        { "bg-sand-100": color === "sand-100" },
        { "bg-sky-100": color === "sky" },
        { "bg-sun-100": color === "sun" },
        rest.className
      )}
    >
      <div className="container max-w-7xl mx-auto flex flex-col md:flex-row justify-between md:items-center gap-6 md:gap-8">
        <Shape
          className={cn(
            "max-h-[120px] md:max-h-[200px] lg:max-h-[272px] self-center md:self-center order-1 md:order-2",
            { "text-sun-100": color === "sand" || color === "sand-100" },
            { "text-sky-25": color === "sky" },
            { "text-sky-100": color === "sun" }
          )}
        />

        <div className="w-full md:w-auto order-2 md:order-1">
          <Headline
            as="h1"
            size="huge"
            className={cn("max-w-lg mb-4", {
              "text-white": color === "sky",
            })}
          >
            {headline}
          </Headline>
          {cta?.label && url && (
            <Link href={url} className="block mt-6 md:mt-12">
              <Button
                variant={color === "sky" ? "white" : "black"}
                size="lg"
                className="w-full md:w-auto"
              >
                {cta.label}
              </Button>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

export { Hero };
